<template>
  <div v-if="loading" class="banner-carousel skeleton-loader"></div>
  <div
    class="banner-carousel"
    v-else-if="bannerImages.length > 0"
    @mouseenter="pauseAutoSlide"
    @mousemove="showNavigationHintTemporarily"
    @mouseleave="resumeAutoSlideWithDelay"
    @touchstart="handleBannerTouchStart($event)"
    @touchend="handleBannerTouchEnd($event)"
    @touchcancel="handleBannerTouchCancel"
    @mousedown="pauseAutoSlide"
    @mouseup="resumeAutoSlideWithDelay"
  >
    <div ref="carouselTrack" class="carousel-track" :style="trackStyle" @transitionend="handleTrackTransitionEnd">
      <div v-for="(banner, index) in displayedBanners" :key="getBannerKey(banner, index)" class="carousel-slide">
        <div class="banner-interaction-layer">
          <button
            type="button"
            class="banner-nav-zone banner-nav-zone-left"
            aria-label="Previous banner"
            @click.stop.prevent="prevSlideAndPause"
          ></button>
          <div v-if="showNavigationHint" class="banner-nav-hint-layer" aria-hidden="true">
            <span class="banner-nav-hint banner-nav-hint-left"></span>
            <span class="banner-nav-hint banner-nav-hint-right"></span>
          </div>
          <a
            href="javascript:void(0)"
            class="banner-center-zone"
            @click.prevent="handleBannerClick(banner)"
            :class="{ 'no-link': !banner.link_url }"
          >
            <picture v-if="banner.image_url_mobile || banner.image_url_desktop" class="banner-picture-wrapper">
                <source media="(min-width: 768px)" :srcset="banner.image_url_desktop" v-if="banner.image_url_desktop">
                <img :src="banner.image_url_mobile || banner.image_url_desktop" class="home-banner" alt="Banner" />
            </picture>
            <div v-else class="home-banner placeholder-banner" :style="{ backgroundColor: banner.background_color || '#f8f9fa' }"></div>
            <div 
              :class="['banner-content', getTextPositionClass(banner.text_position)]" 
              :style="{ color: banner.text_color || '#333333' }" 
              v-if="banner.title || banner.text_content"
            >
                <h2 v-if="banner.title" :style="{ borderColor: banner.text_color || '#333333' }">{{ banner.title }}</h2>
                <p v-if="banner.text_content">{{ banner.text_content }}</p>
            </div>
          </a>
          <button
            type="button"
            class="banner-nav-zone banner-nav-zone-right"
            aria-label="Next banner"
            @click.stop.prevent="nextSlideAndPause"
          ></button>
        </div>
      </div>
    </div>
    <div class="carousel-dots" v-if="bannerImages.length > 1">
      <button
        type="button"
        v-for="(banner, index) in bannerImages" 
        :key="index" 
        class="dot" 
        :class="{ active: normalizedCurrentSlide === index }"
        :aria-label="`Go to banner ${index + 1}`"
        @click.stop.prevent="setSlide(index)"
        @touchstart.stop
        @mousedown.stop
      ></button>
    </div>
  </div>
</template>

<script>
import api from "../../api/payment";

export default {
  name: "ConsumerBanner",
  data() {
    return {
      currentSlide: 0,
      bannerImages: [],
      transitionEnabled: true,
      autoSlideInterval: null,
      autoSlideResumeTimeout: null,
      navigationHintTimeout: null,
      showNavigationHint: false,
      touchStartX: 0,
      touchStartY: 0,
      suppressBannerClick: false,
      loading: true,
    };
  },
  props: {
    bannerData: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    bannerData: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.bannerImages = newVal;
          this.currentSlide = 0;
          this.transitionEnabled = true;
          this.loading = false;
          this.stopAutoSlide();
          if (this.bannerImages.length > 1) {
            this.startAutoSlide();
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    if (this.bannerImages.length > 0) {
        // Already loaded via watch immediate
        return; 
    }
    const cachedBanners = localStorage.getItem("consumer_banners");
    if (cachedBanners) {
      try {
        this.bannerImages = JSON.parse(cachedBanners);
        if (this.bannerImages.length > 0) {
          this.currentSlide = 0;
          this.transitionEnabled = true;
          this.loading = false;
          if (this.bannerImages.length > 1) {
            this.startAutoSlide();
          }
        }
      } catch (e) {
        console.error("Error parsing cached banners:", e);
      }
    }
    this.fetchBanners();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearAutoSlideResumeTimeout();
    this.clearNavigationHintTimeout();
    this.stopAutoSlide();
  },
  computed: {
    hasMultipleBanners() {
      return this.bannerImages.length > 1;
    },
    displayedBanners() {
      if (!this.hasMultipleBanners) {
        return this.bannerImages;
      }

      return [
        this.bannerImages[this.bannerImages.length - 1],
        ...this.bannerImages,
        this.bannerImages[0],
      ];
    },
    normalizedCurrentSlide() {
      if (!this.bannerImages.length) {
        return 0;
      }

      if (this.currentSlide < 0) {
        return this.bannerImages.length - 1;
      }

      if (this.currentSlide >= this.bannerImages.length) {
        return 0;
      }

      return this.currentSlide;
    },
    trackStyle() {
      const displayIndex = this.hasMultipleBanners ? this.currentSlide + 1 : this.currentSlide;

      return {
        transform: `translateX(-${displayIndex * 100}%)`,
        transition: this.transitionEnabled ? 'transform 0.45s ease' : 'none',
      };
    },
  },
  methods: {
    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoSlide();
        return;
      }

      this.recoverCarouselPosition();
      this.resumeAutoSlideWithDelay();
    },
    recoverCarouselPosition() {
      if (!this.hasMultipleBanners) {
        this.currentSlide = 0;
        return;
      }

      const total = this.bannerImages.length;
      if (this.currentSlide === total) {
        this.jumpToRealSlide(0);
        return;
      }

      if (this.currentSlide === -1) {
        this.jumpToRealSlide(total - 1);
        return;
      }

      if (this.currentSlide < -1 || this.currentSlide > total) {
        this.jumpToRealSlide(this.normalizedCurrentSlide);
      }
    },
    getBannerKey(banner, index) {
      return `${index}-${banner.id || banner.image_url_mobile || banner.image_url_desktop || banner.link_url || 'banner'}`;
    },
    getTextPositionClass(position) {
      if (!position) return '';
      return position.toLowerCase().replace(/\s+/g, '-');
    },
    handleBannerClick(banner) {
      if (this.suppressBannerClick) {
        this.suppressBannerClick = false;
        return;
      }

      if (banner && banner.id) {
        api.trackBannerClick({
          banner_id: banner.id,
          link_url: banner.link_url || null,
        }).catch(() => {});
      }

      const link = banner.link_url;
      if (!link) return;

      if (/^https?:\/\//i.test(link)) {
        try {
          const urlObj = new URL(link);
          if (urlObj.host === window.location.host) {
            this.$router.push(urlObj.pathname + urlObj.search + urlObj.hash).catch(() => {});
          } else {
            window.open(link, '_blank');
          }
        } catch (e) {
          window.open(link, '_blank');
        }
      } else {
        const path = link.startsWith('/') ? link : '/' + link;
        this.$router.push(path).catch(() => {});
      }
    },
    fetchBanners() {
      api.getConsumerBanners()
        .then((response) => {
          if (response.code === 200 && response.data) {
            const newBanners = response.data;
            if (JSON.stringify(newBanners) !== JSON.stringify(this.bannerImages)) {
              this.bannerImages = newBanners;
              this.currentSlide = 0;
              this.transitionEnabled = true;
              localStorage.setItem("consumer_banners", JSON.stringify(this.bannerImages));
              
              this.stopAutoSlide();
              if (this.bannerImages.length > 1) {
                this.startAutoSlide();
              }
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching banners:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    nextSlide() {
      if (!this.hasMultipleBanners) {
        return;
      }

      this.recoverCarouselPosition();

      this.transitionEnabled = true;
      if (this.currentSlide === this.bannerImages.length - 1) {
        this.currentSlide = this.bannerImages.length;
        return;
      }

      this.currentSlide += 1;
    },
    prevSlide() {
      if (!this.hasMultipleBanners) {
        return;
      }

      this.recoverCarouselPosition();

      this.transitionEnabled = true;
      if (this.currentSlide === 0) {
        this.currentSlide = -1;
        return;
      }

      this.currentSlide -= 1;
    },
    prevSlideAndPause() {
      this.hideNavigationHint();
      this.pauseAutoSlide();
      this.prevSlide();
      this.resumeAutoSlideWithDelay();
    },
    setSlide(index) {
      this.hideNavigationHint();
      this.transitionEnabled = true;
      this.currentSlide = index;
      this.resumeAutoSlideWithDelay();
    },
    nextSlideAndPause() {
      this.hideNavigationHint();
      this.pauseAutoSlide();
      this.nextSlide();
      this.resumeAutoSlideWithDelay();
    },
    startAutoSlide() {
      if (this.bannerImages.length <= 1 || this.autoSlideInterval) {
        return;
      }

      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 8000);
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
        this.autoSlideInterval = null;
      }
    },
    clearAutoSlideResumeTimeout() {
      if (this.autoSlideResumeTimeout) {
        clearTimeout(this.autoSlideResumeTimeout);
        this.autoSlideResumeTimeout = null;
      }
    },
    pauseAutoSlide() {
      this.clearAutoSlideResumeTimeout();
      this.stopAutoSlide();
    },
    resumeAutoSlideWithDelay() {
      this.clearAutoSlideResumeTimeout();
      this.hideNavigationHint();
      if (this.bannerImages.length <= 1) {
        return;
      }

      this.autoSlideResumeTimeout = setTimeout(() => {
        this.startAutoSlide();
      }, 10000);
    },
    clearNavigationHintTimeout() {
      if (this.navigationHintTimeout) {
        clearTimeout(this.navigationHintTimeout);
        this.navigationHintTimeout = null;
      }
    },
    hideNavigationHint() {
      this.clearNavigationHintTimeout();
      this.showNavigationHint = false;
    },
    showNavigationHintTemporarily() {
      this.clearNavigationHintTimeout();
      if (this.bannerImages.length <= 1) {
        this.showNavigationHint = false;
        return;
      }

      this.showNavigationHint = true;
      this.navigationHintTimeout = setTimeout(() => {
        this.showNavigationHint = false;
      }, 1200);
    },
    handleBannerTouchStart(event) {
      this.pauseAutoSlide();
      this.showNavigationHintTemporarily();
      this.suppressBannerClick = false;
      if (event.touches && event.touches.length > 0) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }
    },
    handleBannerTouchEnd(event) {
      const changedTouch = event.changedTouches && event.changedTouches[0];

      if (!changedTouch) {
        this.resumeAutoSlideWithDelay();
        return;
      }

      const deltaX = changedTouch.clientX - this.touchStartX;
      const deltaY = changedTouch.clientY - this.touchStartY;
      const isHorizontalSwipe = Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY);

      if (isHorizontalSwipe) {
        this.suppressBannerClick = true;
        if (deltaX < 0) {
          this.nextSlideAndPause();
        } else {
          this.prevSlideAndPause();
        }
        return;
      }

      this.resumeAutoSlideWithDelay();
    },
    handleBannerTouchCancel() {
      this.hideNavigationHint();
      this.resumeAutoSlideWithDelay();
    },
    handleTrackTransitionEnd(event) {
      if (!this.hasMultipleBanners || event.target !== event.currentTarget) {
        return;
      }

      if (this.currentSlide === this.bannerImages.length) {
        this.jumpToRealSlide(0);
      } else if (this.currentSlide === -1) {
        this.jumpToRealSlide(this.bannerImages.length - 1);
      } else {
        return;
      }
    },
    jumpToRealSlide(index) {
      this.transitionEnabled = false;
      this.currentSlide = index;

      this.$nextTick(() => {
        const track = this.$refs.carouselTrack;
        if (track) {
          void track.offsetHeight;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.transitionEnabled = true;
          });
        });
      });
    },
  },
};
</script>

<style scoped>
.banner-carousel {
  position: relative;
  overflow: hidden;
  width: 100%;
  border-radius: 15px;
  margin-bottom: 20px;
  width: 100%;
  aspect-ratio: 366 / 150;
  /* height: 180px; Remove fixed height */
}

@media (min-width: 768px) {
  .banner-carousel {
    aspect-ratio: 1280 / 250;
  }
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease-in-out;
  height: 100%;
  will-change: transform;
}

.carousel-slide {
  min-width: 100%;
  box-sizing: border-box;
  position: relative;
}

.banner-interaction-layer {
  position: relative;
  width: 100%;
  height: 100%;
}

.banner-center-zone {
  display: block;
  width: 100%;
  height: 100%;
}

.banner-nav-hint-layer {
  position: absolute;
  inset: 0;
  z-index: 14;
  pointer-events: none;
}

.banner-nav-zone {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 24%;
  padding: 0;
  border: 0;
  background: transparent;
  z-index: 15;
  appearance: none;
}

.banner-nav-zone-left {
  left: 0;
}

.banner-nav-zone-right {
  right: 0;
}

.banner-nav-hint {
  position: absolute;
  top: 50%;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.12);
  backdrop-filter: blur(2px);
}

.banner-nav-hint::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 7px;
  height: 7px;
  border-top: 2px solid rgba(31, 41, 55, 0.68);
  border-right: 2px solid rgba(31, 41, 55, 0.68);
}

.banner-nav-hint-left {
  left: 12px;
  transform: translateY(-50%);
}

.banner-nav-hint-right {
  right: 12px;
  transform: translateY(-50%);
}

.banner-nav-hint-left::before {
  transform: translate(-35%, -50%) rotate(-135deg);
}

.banner-nav-hint-right::before {
  transform: translate(-65%, -50%) rotate(45deg);
}

.home-banner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
}

.placeholder-banner {
  width: 100%;
  height: 100%;
  border-radius: 15px;
}

.banner-content {
    position: absolute;
    /* remove color: white; let inline style handle it */
    text-shadow: none;
    width: 90%;
    max-width: 90%;
    padding: 20px;
    z-index: 10;
    display: flex;
    flex-direction: column;
}



/* Positioning and Alignment Classes */
.banner-content.top-left {
    top: 0;
    left: 0;
    align-items: flex-start;
    text-align: left;
}

.banner-content.top-center {
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    text-align: center;
}

.banner-content.top-right {
    top: 0;
    right: 0;
    align-items: flex-end;
    text-align: right;
}

.banner-content.center-left {
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    align-items: flex-start;
    text-align: left;
}

.banner-content.center-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    align-items: center;
    text-align: center;
}

.banner-content.center-right {
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    align-items: flex-end;
    text-align: right;
}

.banner-content.bottom-left {
    bottom: 0;
    left: 0;
    align-items: flex-start;
    text-align: left;
}

.banner-content.bottom-center {
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    text-align: center;
}

.banner-content.bottom-right {
    bottom: 0;
    right: 0;
    align-items: flex-end;
    text-align: right;
}





.banner-content h2 {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 3px;
    line-height: 1.2;
}

.banner-content p {
    font-size: 12px;
    line-height: 1.2;
    margin-bottom: 0;
}

@media (min-width: 801px) {
    .banner-content h2 {
        font-size: 28px;
        margin-bottom: 5px;
    }

    .banner-content p {
        font-size: 16px;
    }
}



.carousel-dots {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  padding: 2px 6px;
  z-index: 20;
}

.dot {
  width: 8px !important;
  height: 8px !important;
  background-color: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 50%;
  box-sizing: border-box;
  flex: 0 0 8px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
  margin-top: 2px !important;
  padding: 0;
  appearance: none;
}

.dot.active {
  background-color: #1f2937;
  border-color: #1f2937;
  transform: none;
}

.banner-picture-wrapper {
  display: block;
  width: 100%;
  height: 100%;
}
.skeleton-loader {
  background: #E8F5E9; /* Light Green 50 */
  background: linear-gradient(to right, #E8F5E9 8%, #C8E6C9 18%, #E8F5E9 33%);
  background-size: 200% 100%;
  animation: shimmer 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  display: flex;
  justify-content: center;
  align-items: center;
}


@keyframes shimmer {
  0% {
    background-position: -150% 0;
  }
  100% {
    background-position: 150% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
